import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "../lib/utils"
import { Button } from "./ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover"

const EmployeeCombobox = ({
  employees = [],
  value,
  onValueChange,
  placeholder = "Select employee...",
  disabled = false,
  displayFn = null, // Optional custom display function
  emptyMessage = "No employees found.",
  className = ""
}) => {
  const [open, setOpen] = React.useState(false)

  // Default display function
  const defaultDisplayFn = (employee) => {
    if (!employee) return placeholder
    const name = `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
    return name || employee.email || placeholder
  }

  const display = displayFn || defaultDisplayFn

  // Find selected employee
  const selectedEmployee = React.useMemo(() => {
    return employees.find(emp => emp.id === value) || null
  }, [employees, value])

  // Create searchable string for each employee (Command component uses this for filtering)
  const getEmployeeSearchString = (employee) => {
    return `${employee.first_name || ''} ${employee.last_name || ''} ${employee.email || ''} ${employee.position_title || ''}`.toLowerCase()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          {selectedEmployee ? display(selectedEmployee) : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command shouldFilter={true}>
          <CommandInput placeholder="Search employees..." />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {employees.map((employee) => (
                <CommandItem
                  key={employee.id}
                  value={getEmployeeSearchString(employee)}
                  onSelect={() => {
                    onValueChange(employee.id === value ? "" : employee.id)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === employee.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {employee.first_name} {employee.last_name}
                    </span>
                    {employee.email && (
                      <span className="text-xs text-gray-500">{employee.email}</span>
                    )}
                    {employee.position_title && (
                      <span className="text-xs text-gray-500">{employee.position_title}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default EmployeeCombobox

